import React, { useState } from 'react';
import { Sparkles, TrendingUp, Award } from 'lucide-react';

export type UserLevel = 'clean_slate' | 'plateau' | 'polisher';

interface Props {
    onNext: (data: { userLevel: UserLevel }) => void;
    isTransitioning?: boolean;
}

const HonestAssessment: React.FC<Props> = ({ onNext, isTransitioning }) => {
    const [selectedLevel, setSelectedLevel] = useState<UserLevel | undefined>(undefined);

    const handleSelect = (level: UserLevel) => {
        setSelectedLevel(level);
        setTimeout(() => {
            onNext({ userLevel: level });
        }, 350);
    };

    const options: { level: UserLevel; icon: React.ReactNode; title: string; subtitle: string }[] = [
        {
            level: 'clean_slate',
            icon: <Sparkles className="w-6 h-6" />,
            title: '"Čistý štít"',
            subtitle: 'Ještě jsem se na SCIO test ani nepodíval/a',
        },
        {
            level: 'plateau',
            icon: <TrendingUp className="w-6 h-6" />,
            title: '"Plateau"',
            subtitle: 'Jsem zaseklý/á na 50-60 percentilu a nemůžu se prodrat',
        },
        {
            level: 'polisher',
            icon: <Award className="w-6 h-6" />,
            title: '"Elita"',
            subtitle: 'Už se pohybuji v 80. percentilech, jen potřebuji zajistit místo na PFUK',
        },
    ];

    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-right-4 duration-300">
            <div>
                <h3 className="text-gray-900 dark:text-white font-bold text-2xl mb-2">
                    Upřímné zhodnocení (Ego Check)
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
                    Buď upřímný/á — odkud startuješ?
                </p>

                <div className="flex flex-col gap-3 max-w-md mx-auto">
                    {options.map((option) => {
                        const isSelected = selectedLevel === option.level;
                        return (
                            <button
                                key={option.level}
                                onClick={() => handleSelect(option.level)}
                                disabled={isTransitioning}
                                className={`w-full p-4 rounded-xl border-2 border-b-[6px] transition-all duration-200 text-left ${
                                    isSelected
                                        ? 'border-brand-orange bg-[#fed7aa] shadow-sm translate-y-[2px]'
                                        : 'border-gray-300 dark:border-gray-600 hover:border-brand-orange/50 hover:bg-gray-50 dark:hover:bg-gray-800'
                                } active:border-b-2 active:translate-y-[6px] disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`flex-shrink-0 p-2 rounded-lg ${
                                        isSelected 
                                            ? 'bg-brand-orange text-white' 
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {option.icon}
                                    </div>
                                    <div className="flex-1">
                                        <div className={`font-bold text-base mb-1 ${
                                            isSelected ? 'text-brand-orange' : 'text-gray-900 dark:text-white'
                                        }`}>
                                            {option.title}
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-300">
                                            {option.subtitle}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default HonestAssessment;
